import * as React from 'react';
import { Status, StatusType } from 'types/status';
import Alert from 'components/common/alert';
import GrowAnimationContainer from '@ps/payscale-shared-ui/lib/GrowAnimationContainer';
import './statusDisplay.scss';

interface StatusDisplayProps {
    type: StatusType;
    title: string;
    message: string;
    onClose?: () => void;
}

interface StatusDisplayState {
    displayMessage: string;
    displayTitle: string;
    displayType: StatusType;
    show: boolean;
}

class StatusDisplay extends React.PureComponent<StatusDisplayProps, StatusDisplayState> {
    private _displayQueue: Status[];
    private _animating: boolean;

    constructor(props: StatusDisplayProps) {
        super(props);
        this.state = Object.assign({}, this.state, {
            displayMessage: '',
            displayTitle: '',
            displayType: StatusType.None,
            show: false
        } as StatusDisplayState);

        this._displayQueue = [];
        this._animating = false;
        this._onAnimationComplete = this._onAnimationComplete.bind(this);
    }

    componentDidMount(): void {
        const { message, title, type } = this.props;
        this._enqueueStatus(type, title, message);
    }

    UNSAFE_componentWillReceiveProps(nextProps: StatusDisplayProps): void {
        const { message, title, type } = nextProps;
        this._enqueueStatus(type, title, message);
    }

    render(): React.ReactNode {
        const { onClose } = this.props;
        const { displayMessage, displayTitle, displayType, show } = this.state;

        return (
            <GrowAnimationContainer
                fade={true}
                growDown={true}
                show={show}
                onAnimationComplete={this._onAnimationComplete}

            >
                <Alert
                    className="status-display"
                    showCloseButton={true}
                    onClose={onClose}
                    title={displayTitle}
                    type={displayType}
                >
                    {displayMessage}
                </Alert>
            </GrowAnimationContainer>
        );
    }

    _enqueueStatus(type: StatusType, title: string, message: string): void {
        const { show } = this.state;
        if (this._displayQueue.length) {
            // if new status matches last item in queue, no-op/nothing to do
            const last = this._displayQueue[this._displayQueue.length - 1];
            if (message === last.message && title === last.title && type === last.type) {
                return;
            }
        } else if (show) {
            // else if nothing queued, but this status matches current displayed status, do nothing
            const { displayType, displayTitle, displayMessage } = this.state;
            if (message === displayMessage && title === displayTitle && type === displayType) {
                return;
            }
        } else if (type === StatusType.None) {
            // if nothing's queued, and we're already hidden/hiding, ignore
            return;
        }

        this._displayQueue.push({
            message,
            title,
            type
        });

        if (!this._animating) {
            this._onAnimationComplete();
        }
    }

    _onAnimationComplete(): void {
        const { show } = this.state;
        if (this._displayQueue.length === 0) {
            this._animating = false;

            if (!show) {
                // when finish hiding, reset state
                this.setState({
                    displayMessage: '',
                    displayTitle: '',
                    displayType: StatusType.None});
            }
            return;
        }

        this._animating = true;
        const nextStatus = this._displayQueue.pop();
        if (!nextStatus || nextStatus.type === StatusType.None) {
            this.setState({ show: false });
        } else if (show) {
            // if finished showing and next queued item is
            // not a 'hide', then force a hide before showing
            // next item
            this.setState({show: false});
            this._displayQueue.push(nextStatus);
        } else {
            this.setState({
                displayMessage: nextStatus.message,
                displayTitle: nextStatus.title,
                displayType: nextStatus.type,
                show: true
            });
        }
    }
}

export { StatusDisplay };
export default StatusDisplay;


// WEBPACK FOOTER //
// ./src/components/statusDisplay.tsx